<template>
  <div class="home">
    <section>
      <div class="py-8 px-8">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="col-12 col-lg-auto mb-3 mb-lg-0">
            <div class="d-flex align-items-center">
              <h4 class="mb-0 me-2">{{$t('warehouses')}}</h4>
            </div>
          </div>
          <div class="col-12 col-lg-auto d-flex align-items-center gap-2">
            <div class="input-group flex-grow-1" style="border-bottom: 1px solid #ddd;">
              <input class="form-control border-0 search-input" autocomplete="off" type="text" v-model="search" :placeholder="$t('search')">
            </div>
            <button @click="showFilters = !showFilters" class="btn btn-outline-secondary d-md-none">
              <AppIcon name="bi-funnel" class="bi bi-funnel" />
            </button>
          </div>
        </div>
      </div>
    </section>

    <section>
      <div class="px-8">
        <!-- Mobile action buttons -->
        <div class="d-flex d-md-none gap-2 mb-3">
          <template v-if="helper.checkAccess('create_warehouses') != -1">
            <button @click="create" class="btn btn-sm btn-success flex-grow-1">
              <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
              <span>{{$t('warehouse')}}</span>
            </button>
          </template>
        </div>

        <!-- Filters section -->
        <div class="row align-items-end g-2 mb-4" :class="{'d-none d-md-flex': !showFilters}">
          <div class="col d-none d-md-block"></div>
          <div class="col-12 col-md-3 col-lg-2">
            <label class="form-label small d-md-none mb-1">{{$t('filter_by_date')}}</label>
            <select v-model="dateRangeOption" @change="handleDateRangeChange" class="form-select form-select-sm">
              <option value="">{{$t('filter_by_date')}}</option>
              <option value="today">{{$t('today')}}</option>
              <option value="yesterday">{{$t('yesterday')}}</option>
              <option value="this_week">{{$t('this_week')}}</option>
              <option value="last_week">{{$t('last_week')}}</option>
              <option value="this_month">{{$t('this_month')}}</option>
              <option value="custom">{{$t('custom_date')}}</option>
            </select>
          </div>
          <div class="col-12 col-md-3 col-lg-2" v-if="dateRangeOption === 'custom'">
            <label class="form-label small d-md-none mb-1">{{$t('custom_date')}}</label>
            <date-picker class="w-100" :clearable="true" :editable="false" v-model="range" type="date" range :multi-calendars="1" :placeholder="$t('select_date_range')"></date-picker>
          </div>
          <div class="col-md-2 d-none d-md-block">
            <template v-if="helper.checkAccess('create_warehouses') != -1">
              <button @click="create" class="btn btn-sm btn-success w-100">
                <PlusIcon class="me-1 client-lucide-icon" :size="15" :stroke-width="1.75" aria-hidden="true" />
                <span>{{$t('warehouse')}}</span>
              </button>
            </template>
          </div>
        </div>

        <div class="page-table-container mb-6 bg-white rounded shadow">
          <div class="table-responsive">
            <table class="table mb-0 table-borderless table-striped small">
              <thead>
                <tr class="text-secondary">
                  <th class="pt-4 pb-3 px-6" style="width: 40px;">
                      <div class="form-check mb-0">
                          <input class="form-check-input" type="checkbox" :checked="areAllSelected" @change="toggleSelectAll">
                      </div>
                  </th>
                  <sortable-table-header field="reference" label="reference" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getWarehouses)" />
                  <sortable-table-header field="name" label="name" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getWarehouses)" />
                  <sortable-table-header field="address" label="address" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getWarehouses)" />
                  <sortable-table-header field="created_at" label="created_at" :sort-field="sortField" :sort-direction="sortDirection" @sort="applySort($event, getWarehouses)" />
                  <th class="pt-4 pb-3 px-6">{{$t('action')}}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="warehouse in warehouses" :key="warehouse.id" :class="{'table-active': warehouse.selected}">
                  <td class="py-5 px-6">
                      <div class="form-check mb-0">
                          <input class="form-check-input" type="checkbox" v-model="warehouse.selected" @change="changeSelectedItem">
                      </div>
                  </td>
                  <td class="py-5 px-6"><router-link  :to="`/warehouses/${warehouse.id}`"><b>{{warehouse.reference }}</b></router-link></td>
                  <td class="py-5 px-6">{{warehouse.name}}</td>
                  <td class="py-5 px-6">{{warehouse.address}}</td>
                  <td class="py-5 px-6">{{formatDate(warehouse.created_at)}}</td>
                  <td class="py-5 px-6">
                    <router-link :to="`/warehouses/${warehouse.id}`" style="padding: 5px;margin: 0 2px 0 0;font-size: 12px !important;" class="btn btn-outline-primary btn-xs">
                        <AppIcon name="bi-eye" class="bi bi-eye" style="font-size:12px" />
                        {{$t('details')}}
                    </router-link>
                    <button v-if="helper.checkAccess('edit_warehouses') != -1" @click="editRow(warehouse)" style="padding: 5px;margin: 0;font-size: 12px !important;margin-right:2px" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
                        {{$t('edit')}}
                    </button>
                    <button v-if="helper.checkAccess('delete_warehouses') != -1" @click="deleteRow(warehouse)" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs">
                        <AppIcon name="bi-trash" class="bi bi-trash" style="font-size:12px" />
                        {{$t('delete')}}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <table-loading-bar v-if="loading" class="page-table-state text-center" />
          <div v-if="!loading && !warehouses.length && !isFiltered" class="page-table-state text-center py-5 my-5 bg-white rounded shadow-sm border" style="border-style: dashed !important;">
            <AppIcon name="bi-building" class="bi bi-building text-muted" style="font-size: 3rem;" />
            <h4 class="mt-3">{{ $t('no_warehouses_added') }}</h4>
            <p class="text-muted">{{ $t('start_by_adding_warehouses') }}</p>
            <template v-if="helper.checkAccess('create_warehouses') != -1">
              <button class="btn btn-outline-primary mt-2" @click="create">
                {{ $t('create_first_warehouse') }}
              </button>
            </template>
          </div>
          <div v-if="!loading && !warehouses.length && isFiltered" class="page-table-state text-center" style="padding:1rem">{{$t('no_warehouse_found')}}.</div>
        </div>
      </div>


        <!-- Bulk Action Bar -->
        <div v-if="isSelectedItem" class="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow rounded d-flex align-items-center gap-3" style="z-index: 1050; border: 1px solid #dee2e6; min-width: 300px; justify-content: space-between;">
            <div class="d-flex align-items-center">
                <span class="fw-bold me-2">{{ selectedCount }}</span>
                <span class="text-muted">{{$t('selected')}}</span>
            </div>
            <div class="d-flex gap-2">
                <button v-if="helper.checkAccess('delete_warehouses') != -1" class="btn btn-danger btn-sm d-flex align-items-center" @click="deleteSelected">
                    <AppIcon name="bi-trash" class="bi bi-trash me-1" /> {{$t('delete')}}
                </button>
            </div>
        </div>
    </section>

    <!-- Modals -->
    <div class="modal fade" id="create-modal" tabindex="-1" aria-labelledby="create-modal" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{$t('create_warehouse')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{$t('name')}}</label>
              <input v-model="warehouse.name" type="text" class="form-control" placeholder="Warehouse Name">
            </div>
            <div class="mb-3">
              <label class="form-label">{{$t('address')}}</label>
              <input v-model="warehouse.address" type="text" class="form-control" placeholder="Warehouse Address">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
            <button type="button" class="btn btn-success" :disabled="loading || warehouse.name.length < 2" @click="save"><AppIcon name="bi-check-circle" class="bi bi-check-circle me-2" />{{$t('create')}}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="modal fade" id="edit-modal" tabindex="-1" aria-labelledby="edit-modal" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{$t('edit_warehouse')}}</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">{{$t('name')}}</label>
              <input v-model="warehouse.name" type="text" class="form-control" placeholder="Warehouse Name">
            </div>
            <div class="mb-3">
              <label class="form-label">{{$t('address')}}</label>
              <input v-model="warehouse.address" type="text" class="form-control" placeholder="Warehouse Address">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
            <button type="button" class="btn btn-success" :disabled="loading || warehouse.name.length < 2" @click="update"><AppIcon name="bi-check-circle" class="bi bi-check-circle me-2" />{{$t('edit')}}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import moment from 'moment';
import sortableList from '../../mixins/sortableList'
export default {
  name: 'Warehouse',
  persistedFilterState: {
    key: 'warehouses',
    fields: ['search', 'dateRangeOption', 'range', 'sortField', 'sortDirection'],
    dateFields: ['range'],
  },
  mixins: [sortableList],
  components: { DatePicker },
  data() {
    return {
      search: '',
      warehouses: [],
      warehouse: { id: null, name: '', address: '' },
      range: [],
      loading: false,
      createModal: null,
      editModal: null,
      dateRangeOption: '',
      showFilters: false,
      isSelectedItem: false
    };
  },
  mounted() {
    window.mixpanel.track("warehouse");

    //check permissions
    if(!helper.checkModuleIsEnabled('warehouses')) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_warehouses') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_warehouses') == -2) return this.$router.push({path : '/subscribe'})

    // Initialize this_month date range
    // this.handleDateRangeChange()
    
    this.getWarehouses();

    // eslint-disable-next-line no-undef
    this.createModal = new bootstrap.Modal(document.getElementById('create-modal'));
    // eslint-disable-next-line no-undef
    this.editModal = new bootstrap.Modal(document.getElementById('edit-modal'));
  },
  methods: {
        formatDate(date) { return helper.formatDate(date) },
    create() {
      this.clearForm();
      this.createModal.show();
    },
    editRow(row) {
      this.warehouse = row;
      this.editModal.show();
    },
    async save() {
      try {
        this.loading = true;
        await helper.customPost(`${process.env.VUE_APP_API}/warehouses`, this.warehouse);
        this.loading = false;
        this.getWarehouses();
        this.createModal.hide();
      } catch (error) {
        this.loading = false;
      }
    },
    async update() {
      try {
        this.loading = true;
        await helper.customPatch(`${process.env.VUE_APP_API}/warehouses/${this.warehouse.id}`, this.warehouse);
        this.loading = false;
        this.getWarehouses();
        this.editModal.hide();
      } catch (error) {
        this.loading = false;
      }
    },
    async deleteRow(row) {
      if (confirm(this.$t('are_you_sure'))) {
        await helper.customDelete(`${process.env.VUE_APP_API}/warehouses/${row.id}`);
        this.warehouses = helper.withoutDeletedRecords(this.warehouses, row.id);
        this.count = Math.max(0, this.count - 1);
      }
    },
    async getWarehouses(page) {
      try {
        
        this.loading = true
        this.page = page || 1
        const params = new URLSearchParams({ page: this.page, per_page: this.per_page || 25 })
        this.appendSortParams(params)
        if (this.search) params.append('keyword', this.search)
        const response = await helper.customGet(`${process.env.VUE_APP_API}/warehouses?${params.toString()}`);
        this.loading = false
            this.warehouses = response.data.data
            
            for(let i = 0 ; i < this.warehouses.length ; i++){
                this.$set(this.warehouses[i], 'selected', false)
            }

            this.count = response.data.count
            if(this.per_page != response.data.per_page){
                this.per_page = response.data.per_page
            }

            //pagination
            this.pagination = []
            let MaxPagination = Math.ceil(this.count / this.per_page )
            for(let i = 1 ; i <= MaxPagination ;i++){
                this.pagination.push(i)
            }
      } catch (error) {
        this.loading = false;
      }
    },
    clearForm() {
      this.warehouse = { id: null, name: '', address: '' };
    },
     // Handle date range selection
    handleDateRangeChange() {
      if (this.dateRangeOption === '') {
        this.range = []
        this.getWarehouses()
      } else if (this.dateRangeOption === 'today') {
        const start = new Date()
        const end = new Date()
        this.range = [start, end]
      } else if (this.dateRangeOption === 'yesterday') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24)
        end.setTime(end.getTime() - 3600 * 1000 * 24)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'last_week') {
        const start = new Date()
        const end = new Date()
        start.setTime(start.getTime() - 3600 * 1000 * 24 * 14)
        end.setTime(end.getTime() - 3600 * 1000 * 24 * 7)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'this_month') {
        const start = new Date()
        const end = new Date()
        start.setDate(1)
        this.range = [start, end]
      } else if (this.dateRangeOption === 'custom') {
        this.range = []
      }
    },
    changeSelectedItem(){
        this.isSelectedItem = this.warehouses.find(warehouse => warehouse.selected === true ) ? true : false
    },
    toggleSelectAll() {
      const newState = !this.areAllSelected;
      this.warehouses.forEach(w => w.selected = newState);
      this.changeSelectedItem();
    },
    async deleteSelected() {
      const selectedIds = this.warehouses.filter(w => w.selected).map(w => w.id);
      if (selectedIds.length === 0) return;

      this.$swal.fire({
        title: `${this.$t('are_you_sure')}?`,
        text: this.$t('you_cant_revert'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: this.$t('yes_delete_it')
      }).then(async (result) => {
        if (result.isConfirmed) {
            this.loading = true;
            try {
                const results = await Promise.allSettled(selectedIds.map(id => helper.customDelete(`${process.env.VUE_APP_API}/warehouses/${id}`)));
                const deletedIds = helper.fulfilledRecordIds(selectedIds, results);
                this.warehouses = helper.withoutDeletedRecords(this.warehouses, deletedIds);
                this.count = Math.max(0, this.count - deletedIds.length);
                if (deletedIds.length !== selectedIds.length) throw results.find(result => result.status === 'rejected').reason;
                this.isSelectedItem = false;
                this.$toast.open({
                    message: this.$t('warehouses_deleted_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
            } catch (error) {
                this.$toast.open({
                    message: this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            } finally {
                this.loading = false;
            }
        }
      });
    }
  },
  watch : {
    async search(){
        if(this.search.length > 2){
            try{
                this.loading = true
                const response = await helper.customPost(`${process.env.VUE_APP_API}/warehouses/search`, {keyword : this.search})
                this.warehouses = response.data.data
                this.count = response.data.count
                this.loading = false
            }catch(error){
                this.loading = false
            }
        }
        if(this.search == '') return this.getWarehouses()
    },
    range(){
        

        if(this.range[0] == null){
            this.start = null
            this.end = null
            this.getWarehouses()
        }

        if(this.range.length && this.range[0] && this.range[1]){
            this.start = moment(this.range[0]).format('YYYY-MM-DD')
            this.end = moment(this.range[1]).format('YYYY-MM-DD')
            this.getWarehouses()
        }else{
            this.warehouses = []
        }
    },
    per_page(){
        this.getWarehouses(this.page)
    },
   
  },
  computed : {
    isFiltered() {
      return (this.dateRangeOption && this.dateRangeOption !== '') ||
             (this.search && this.search.length > 0) ||
             (this.range && this.range.length > 0);
    },
    role(){ return this.$store.getters.ROLE },
    permissions(){ return this.$store.getters.PERMISSIONS },
    business(){ return this.$store.getters.BUSINESS },
    helper(){ return helper },
    areAllSelected() {
      return this.warehouses.length > 0 && this.warehouses.every(w => w.selected);
    },
    selectedCount() {
      return this.warehouses.filter(w => w.selected).length;
    }
  }
};
</script>

<style scoped>
.table-borderless>:not(caption)>*>* {
  padding: 5px !important;
}
</style>
